import { useState, useRef, useEffect, useCallback, FormEvent } from "react";
import { Search, Sparkles, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import GoKeycap from "@/components/GoKeycap";
import { EXAMPLE_PROMPTS } from "@/data/mockData";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileSearchOverlay from "@/components/MobileSearchOverlay";
import { pushSessionRecent } from "@/lib/sessionRecents";
import { prefetchSearch } from "@/lib/prefetchSearch";

// Curated placeholders kept short enough to render fully inside the search bar
// at narrow mobile widths (430px). Anything that would overrun is filtered out.
const MAX_PLACEHOLDER_LEN = 46;
const ROTATING_PLACEHOLDERS: string[] = (() => {
  const fits = EXAMPLE_PROMPTS.filter((p) => p.length <= MAX_PLACEHOLDER_LEN);
  const shuffled = [...fits].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 10);
})();

// Typewriter timing (ms)
const TYPE_SPEED = 55;
const BACKSPACE_SPEED = 25;
const HOLD_AFTER_TYPE = 1200; // pause before cursor blinks/backspace
const BLINK_INTERVAL = 480;   // half-cycle: on→off or off→on
const BLINK_COUNT = 6;        // 3 full blinks (on/off pairs)
const PAUSE_BEFORE_NEXT = 400;

interface SearchBarProps {
  size?: "large" | "compact";
  initialQuery?: string;
  onSearch?: (query: string) => void;
  onFocusChange?: (focused: boolean) => void;
  /** Called when the search enters/exits "spotlight" mode (focused with a query or suggestions). */
  onSpotlightChange?: (active: boolean) => void;
  /** When this value changes, the search box is filled with it and focused (without auto-submitting). */
  injectedQuery?: string;
  /** Clear the input as soon as the user focuses/clicks the bar. Useful on
   *  results pages where the user almost always wants to type a new query. */
  clearOnFocus?: boolean;
}


interface Suggestion {
  text: string;
  source: "log" | "ai";
}

export default function SearchBar({ size = "large", initialQuery = "", onSearch, onFocusChange, onSpotlightChange, injectedQuery, clearOnFocus }: SearchBarProps) {
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [typedText, setTypedText] = useState("");
  const [caretOn, setCaretOn] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const clearingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // Mobile only: when true, render the fullscreen Google-style overlay
  // instead of the inline dropdown. Desktop keeps the current centered UX.
  const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);

  const updateFocus = useCallback((focused: boolean) => {
    setIsFocused(focused);
    onFocusChange?.(focused);
  }, [onFocusChange]);

  // Forward-ref for fetchSuggestions so the injectedQuery effect can call it
  // without creating a TDZ on the useCallback declared further below.
  const fetchSuggestionsRef = useRef<((partial: string) => void) | null>(null);

  // Fill the box when an external prompt is injected (e.g. user clicks teaser).
  // Mirrors the "user typed it" flow: focuses the input AND fetches suggestions
  // so trending/teaser picks show the same autocomplete + AI-suggested searches.
  useEffect(() => {
    if (injectedQuery == null) return;
    // Strip optional "\u0000<n>" cache-buster suffix the parent uses to re-trigger.
    const value = injectedQuery.split("\u0000")[0];
    setQuery(value);
    updateFocus(true);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        try { el.setSelectionRange(len, len); } catch { /* noop */ }
      }
    });
    // Trigger suggestions as if the user had typed this query.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestionsRef.current?.(value), 150);
  }, [injectedQuery, updateFocus]);

  // Clear any pending fade-out timeout if the component unmounts or focus
  // is interrupted, so we don't leak timers or clear state unexpectedly.
  useEffect(() => () => {
    if (clearingTimeoutRef.current) {
      clearTimeout(clearingTimeoutRef.current);
      clearingTimeoutRef.current = null;
    }
  }, []);

  // Typewriter state machine: type → hold → blink 3× → backspace → next.
  // While focused (or query has text), the placeholder fades out but the
  // animation pauses in place — when the user clicks away it fades back in
  // and resumes from the exact character it was on.
  const phaseRef = useRef<"typing" | "blinking" | "backspacing" | "pausing">("typing");
  const typedLenRef = useRef(0);
  useEffect(() => { typedLenRef.current = typedText.length; }, [typedText]);

  // Hoisted so the typewriter effect can depend on a stable boolean instead
  // of the full `query` string — keystrokes within an already-non-empty query
  // no longer re-trigger the effect cleanup/setup cycle.
  const isLarge = size === "large";
  const hasQuery = query.length > 0;

  useEffect(() => {
    // Pause while focused or typing — but DO NOT reset typedText/caret so we
    // can resume from the same character when focus is released.
    if (isFocused || hasQuery) {
      return;
    }
    const reduced = typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const target = ROTATING_PLACEHOLDERS[placeholderIndex] ?? "Ask an ecommerce question…";

    if (reduced) {
      setTypedText(target);
      setCaretOn(true);
      const t = setTimeout(() => {
        setTypedText("");
        setPlaceholderIndex((i) => (i + 1) % ROTATING_PLACEHOLDERS.length);
      }, 3500);
      return () => clearTimeout(t);
    }

    const timers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    const schedule = (fn: () => void, delay: number) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
      timers.push(id);
    };

    // Resume position: figure out where in the cycle we left off based on the
    // last typed length. If we're mid-word, continue typing from that index.
    // If complete or empty, run the full cycle.
    const startLen = Math.min(typedLenRef.current, target.length);
    const startsMidWord = startLen > 0 && startLen < target.length &&
      target.slice(0, startLen) === (typedText || "").slice(0, startLen);

    setCaretOn(true);
    if (!startsMidWord) {
      // Fresh cycle — clear and type from scratch.
      setTypedText("");
    }

    const typeFromIndex = startsMidWord ? startLen : 0;
    // Phase 1: type forward from current position
    for (let i = typeFromIndex + 1; i <= target.length; i++) {
      const step = i - typeFromIndex;
      schedule(() => setTypedText(target.slice(0, i)), step * TYPE_SPEED);
    }
    const typingDuration = (target.length - typeFromIndex) * TYPE_SPEED;

    // Phase 2: hold + blink 3 full times (BLINK_COUNT half-cycles)
    const afterTypeAt = typingDuration + HOLD_AFTER_TYPE;
    for (let b = 0; b < BLINK_COUNT; b++) {
      schedule(() => setCaretOn((c) => !c), afterTypeAt + b * BLINK_INTERVAL);
    }

    // Phase 3: backspace
    const backspaceStart = afterTypeAt + BLINK_COUNT * BLINK_INTERVAL;
    schedule(() => setCaretOn(true), backspaceStart);
    for (let i = target.length - 1; i >= 0; i--) {
      const step = target.length - i;
      schedule(() => setTypedText(target.slice(0, i)),
        backspaceStart + step * BACKSPACE_SPEED);
    }

    // Phase 4: advance to next placeholder
    const endAt = backspaceStart + target.length * BACKSPACE_SPEED + PAUSE_BEFORE_NEXT;
    schedule(() => {
      setPlaceholderIndex((i) => (i + 1) % ROTATING_PLACEHOLDERS.length);
    }, endAt);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, hasQuery, placeholderIndex]);

  // Coalesce textarea auto-resize into a single rAF tick. Rapid focus/typing
  // events that fire within the same frame produce only one layout pass,
  // which removes the sub-pixel height jitter you'd otherwise see.
  const resizeRafRef = useRef<number | null>(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (resizeRafRef.current != null) cancelAnimationFrame(resizeRafRef.current);
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      el.style.height = 'auto';
      // Grow with content but never collapse below the CSS min-height — keeps
      // the bar at a stable height when focusing/blurring an empty input.
      const min = parseFloat(getComputedStyle(el).minHeight) || 0;
      const next = Math.max(el.scrollHeight, min);
      // Only write when the value actually changes (sub-pixel safe) to avoid
      // triggering unnecessary style recalcs.
      if (Math.abs(parseFloat(el.style.height) - next) > 0.5) {
        el.style.height = next + 'px';
      } else {
        el.style.height = next + 'px';
      }
    });
    return () => {
      if (resizeRafRef.current != null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    };
  }, [query]);

  // Spotlight = user is actively typing in the results-page search bar.
  // Blur only once they have entered text so the autocomplete dropdown is
  // readable against a muted background, but the bar stays in its natural spot.
  const spotlightActive = isFocused && hasQuery;
  useEffect(() => {
    onSpotlightChange?.(spotlightActive);
  }, [spotlightActive, onSpotlightChange]);

  // Restore normal state on outside click: hide suggestions, and blur the
  // search bar so the spotlight (and page blur) turns off.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
        textareaRef.current?.blur();
        updateFocus(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [updateFocus]);

  const fetchSuggestions = useCallback(async (partial: string) => {
    if (partial.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoadingSuggestions(true);
    try {
      const { data, error } = await supabase.functions.invoke("suggest-queries", {
        body: { partial: partial.trim() },
      });

      if (error) {
        console.error("Suggest error:", error);
        setSuggestions([]);
        return;
      }

      const source = data?.source || "logs";
      const items: Suggestion[] = (data?.suggestions || []).map((text: string, i: number) => ({
        text,
        source: source === "hybrid" && i >= (data?.suggestions?.length - 2) ? "ai" : "log",
      }));

      setSuggestions(items);
      setShowSuggestions(items.length > 0);
      setSelectedIndex(-1);
    } catch (err) {
      console.error("Suggest fetch error:", err);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  // Keep the ref in sync so the injectedQuery effect can call the latest impl.
  useEffect(() => { fetchSuggestionsRef.current = fetchSuggestions; }, [fetchSuggestions]);

  const handleChange = (value: string) => {
    // If the user starts typing during a results-page fade-out, immediately
    // cancel the animation and show their keystrokes.
    if (isClearing) {
      if (clearingTimeoutRef.current) {
        clearTimeout(clearingTimeoutRef.current);
        clearingTimeoutRef.current = null;
      }
      setIsClearing(false);
    }
    setQuery(value);
    if (value.trim().length > 0) updateFocus(true);
    else updateFocus(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // 200ms feels snappier than 500ms while still coalescing fast typing.
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 200);
  };

  // Results-page UX: clear the previous query with a quick fade so the
  // transition feels intentional, not like a glitch.
  const clearQuery = useCallback(() => {
    if (clearingTimeoutRef.current) clearTimeout(clearingTimeoutRef.current);
    setIsClearing(true);
    clearingTimeoutRef.current = setTimeout(() => {
      setQuery("");
      setSuggestions([]);
      setSelectedIndex(-1);
      setShowSuggestions(false);
      setIsClearing(false);
      clearingTimeoutRef.current = null;
    }, 150);
  }, []);

  const submitSearch = useCallback((q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setShowSuggestions(false);
    setSelectedIndex(-1);
    textareaRef.current?.blur();
    updateFocus(false);
    pushSessionRecent(trimmed);
    // Kick the network the instant the user commits — by the time
    // SearchResults mounts, the NDJSON stream is already in flight (often
    // already cached), so the page paints from a fresh cache hit.
    prefetchSearch(trimmed);
    if (onSearch) {
      onSearch(trimmed);
    } else {
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    }
  }, [navigate, onSearch, updateFocus]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitSearch(query);
  };

  const selectSuggestion = (text: string) => {
    setQuery(text);
    submitSearch(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      // Restore normal state: hide suggestions, blur the input, and exit spotlight.
      e.preventDefault();
      setShowSuggestions(false);
      setSelectedIndex(-1);
      textareaRef.current?.blur();
      updateFocus(false);
      e.stopPropagation();
      return;
    }

    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (selectedIndex >= 0) {
        selectSuggestion(suggestions[selectedIndex].text);
      } else {
        handleSubmit(e);
      }
    }
  };


  const handleGoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    submitSearch(query);
  };

  return (
    <div ref={containerRef} className="w-full max-w-2xl mx-auto relative z-20">
      <form onSubmit={handleSubmit}>
        <div
          className={`flex items-center retro-border bg-[hsl(var(--input))] py-1 ${
            isLarge ? "retro-shadow" : "retro-shadow-sm"
          }`}
        >
          <Search className={`text-accent flex-shrink-0 ml-2 sm:ml-3 self-center ${isLarge ? "w-5 h-5 sm:w-6 sm:h-6" : "w-4 h-4 sm:w-5 sm:h-5"}`} />
          <div className="relative flex-1 min-w-0">
            {/* Typewriter placeholder — types in, blinks, backspaces. Display only. */}
            {!query && (
              <div
                aria-hidden="true"
                className={`absolute inset-0 flex items-center pl-2 sm:pl-3 pr-6 sm:pr-8 font-mono leading-snug text-ink-blue-light pointer-events-none overflow-hidden transition-opacity duration-300 ease-out ${
                  isFocused ? "opacity-0" : "opacity-100"
                } ${isLarge ? "text-base sm:text-xl md:text-2xl" : "text-sm sm:text-base"}`}
              >
                <span className="block min-w-0 whitespace-nowrap truncate">
                  {typedText}
                  <span
                    aria-hidden="true"
                    className={`text-ink-blue-light transition-opacity duration-200 ease-in-out ${
                      caretOn ? "opacity-90" : "opacity-0"
                    }`}
                  >
                    _
                  </span>
                </span>
              </div>
            )}
            <textarea
              ref={textareaRef}
              aria-label="Search ecommerce podcast episodes"
              value={query}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={(e) => {
                // Results-page UX: wipe the previous query with a soft fade so
                // the clear feels intentional, not like a glitch.
                if (clearOnFocus && query && !isClearing) {
                  clearQuery();
                }
                // Mobile: hijack focus and open the fullscreen overlay so the
                // bar pins to the top above the keyboard. Blur the underlying
                // textarea so the OS keyboard doesn't open against it.
                if (isMobile && isLarge) {
                  e.currentTarget.blur();
                  setMobileOverlayOpen(true);
                  updateFocus(true);
                  return;
                }
                updateFocus(true);
                if (suggestions.length > 0) setShowSuggestions(true);
              }}
              // Mobile: opening the soft-keyboard via tap fires `click` before
              // `focus` on some browsers; mirror the same hijack here so the
              // overlay opens reliably on first tap.
              onClick={() => {
                if (clearOnFocus && query && !isClearing) {
                  clearQuery();
                }
                if (isMobile && isLarge && !mobileOverlayOpen) {
                  textareaRef.current?.blur();
                  setMobileOverlayOpen(true);
                  updateFocus(true);
                }
              }}
              onBlur={() => updateFocus(false)}
              placeholder={isFocused ? "Ask an ecommerce question…" : ""}
              rows={1}
              readOnly={isMobile && isLarge}
              className={`block w-full bg-transparent placeholder:text-ink-blue-light placeholder:font-normal placeholder:opacity-90 focus:outline-none px-2 sm:px-3 font-mono font-normal leading-snug text-ink-blue resize-none overflow-hidden box-border transition-opacity duration-150 ${
                isClearing ? "opacity-0" : "opacity-100"
              } ${
                isLarge ? "text-base sm:text-xl md:text-2xl py-3 sm:py-4 min-h-[3.25rem] sm:min-h-[4rem]" : "text-sm sm:text-base py-2 min-h-[2.25rem]"
              }`}
            />
          </div>
          {/* Mechanical keyboard GO keycap */}
          <div
            className={`flex-shrink-0 mr-1.5 sm:mr-2.5 self-center transition-all duration-200 ${
              hasQuery ? "opacity-100 scale-100" : "opacity-0 scale-75 w-0 overflow-hidden pointer-events-none"
            }`}
          >
            <GoKeycap onClick={handleGoClick} size={size} />
          </div>
        </div>

        {/* Suggestions dropdown — absolute overlay so it never pushes
            surrounding layout (which would make the search bar appear to
            jump up/down when suggestions arrive). */}
        <div className={`absolute left-0 right-0 top-full z-30 mt-1 retro-border retro-shadow-sm bg-card overflow-hidden transition-all duration-300 ease-out ${
          showSuggestions && suggestions.length > 0 ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0 border-transparent shadow-none !mt-0 pointer-events-none"
        }`}>
          {suggestions.map((suggestion, i) => (
            <button
              key={i}
              type="button"
              onClick={() => selectSuggestion(suggestion.text)}
              className={`w-full flex items-center gap-2.5 px-3 sm:px-4 py-2.5 text-left font-mono text-xs sm:text-sm transition-colors ${
                i === selectedIndex
                  ? "bg-accent/20 text-accent-foreground"
                  : "hover:bg-muted"
              } ${i > 0 ? "border-t border-border" : ""}`}
            >
              {suggestion.source === "ai" ? (
                <Sparkles className="w-3.5 h-3.5 text-accent flex-shrink-0" />
              ) : (
                <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              )}
              <span className="truncate">{suggestion.text}</span>
            </button>
          ))}
          {loadingSuggestions && (
            <div className="px-4 py-2 text-[10px] font-mono text-muted-foreground border-t border-border">
              Finding more suggestions…
            </div>
          )}
        </div>

      </form>

      {/* Mobile fullscreen Google-style search experience.
          Only mounted on small viewports for the large (homepage) variant. */}
      {isMobile && isLarge && (
        <MobileSearchOverlay
          open={mobileOverlayOpen}
          initialQuery={query}
          onClose={() => {
            // "Back" arrow → restore home exactly: clear the bar and unfocus.
            setMobileOverlayOpen(false);
            setQuery("");
            setSuggestions([]);
            setShowSuggestions(false);
            updateFocus(false);
          }}
          onSubmit={(q) => {
            setMobileOverlayOpen(false);
            setQuery(q);
            setShowSuggestions(false);
            pushSessionRecent(q);
            prefetchSearch(q);
            if (onSearch) {
              onSearch(q);
            } else {
              navigate(`/search?q=${encodeURIComponent(q)}`);
            }
          }}
        />
      )}
    </div>
  );
}
